import { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignupApi } from '../api';
import { errorMessage } from '../helpers/apiErrors';
import { getSignupToken } from '../helpers/signupSession';
import { signupAccountValidationSchema } from '../helpers/validation';
import { useSignupForm } from '../hooks';

export type SignupAccountFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
};

const EMPTY_FORM: SignupAccountFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
};

/**
 * The first thing the funnel asks for. Five fields rather than eighteen, and the address among them is what
 * makes an abandonment here a lead rather than a row nobody can be reached at.
 */
export const useSignupAccountViewController = () => {
  const api = useSignupApi();
  const navigate = useNavigate();

  const {
    formValues,
    setFormValues,
    errors,
    formError,
    setFormError,
    submitting,
    setSubmitting,
    handleChange,
    validate,
  } = useSignupForm<SignupAccountFormValues>(EMPTY_FORM, signupAccountValidationSchema);

  // The prefill below must lose to the client: the read is fired on mount, and an answer arriving a keystroke
  // later would take the name back out of the field it was being typed into.
  const touched = useRef(false);

  const handleFieldChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
      touched.current = true;
      handleChange(e);
    },
    [handleChange],
  );

  /**
   * Nothing on this screen can be saved without a funnel to save it to, and the plan list is what starts one.
   * A funnel that has already been here is read back into the form: the ordinary reason for coming back is a
   * mistyped address, and retyping the other three fields to correct it is what the plan screen's Continue
   * would otherwise cost. The password is not among them — it is not handed back, only replaced.
   *
   * And the funnel is asked whether this step is still open: the confirmation freezes these details, and the
   * screen is reachable past it by the Back button and by the code screen's way out of a mistyped address.
   * Ungated it takes four answers the API then refuses, which reads as "try again" for a lost cause.
   */
  useEffect(() => {
    if (getSignupToken() == null) {
      navigate('/signup');
      return;
    }

    let active = true;
    api
      .getState()
      .then(({ data }) => {
        if (!active) return;
        if (!data.allowedActions.includes('save_account')) {
          navigate(data.allowedActions.includes('save_company_info') ? '/signup/company' : '/signup');
          return;
        }

        if (touched.current) return;
        setFormValues(current => ({
          ...current,
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          email: data.email ?? '',
          phone: data.phone ?? '',
        }));
      })
      .catch(() => {
        // A convenience, not a condition of the form: the fields are simply left empty. Not a gate either —
        // an unanswered read must not move a client off the step they are standing on.
      });

    return () => {
      active = false;
    };
  }, [api, navigate, setFormValues]);

  const submit = useCallback(async() => {
    if (!await validate(formValues)) return;

    setSubmitting(true);
    setFormError(null);
    try {
      await api.saveAccount(formValues);
    } catch (e) {
      setFormError(errorMessage(e, 'Your details could not be saved. Please try again.'));
      setSubmitting(false);
      return;
    }

    try {
      // Sending the code is part of finishing this step: the next screen only asks for what the email holds,
      // and arriving there with nothing sent offers six digits nobody has.
      await api.sendVerification();
      navigate('/signup/verify');
    } catch (e) {
      // The details are saved by the time this can fail, so the complaint must not be about them. Pressing
      // Continue again writes the same answers over and asks for the code a second time.
      setFormError(errorMessage(e, 'We saved your details but could not email your code. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }, [api, formValues, navigate, setFormError, setSubmitting, validate]);

  return {
    formValues,
    errors,
    formError,
    submitting,
    handleChange: handleFieldChange,
    submit,
  };
};
